import type { EdgeTypeId, EdgeData } from '@/domain/funnel/edge-system';
import { EDGE_TYPE_IDS } from '@/domain/funnel/edge-system';

export type EdgePropertyKey = 'label' | 'type' | 'style' | 'animated' | 'colorOverride';
export interface EdgePropertyFieldDefinition {
  key: EdgePropertyKey;
  label: string;
}

/**
 * SPEC-006 EDGE-REQ-001/002 — edge types are defined in a registry so the
 * UI never needs a `switch(edge.type)` spread across components.
 */
export interface EdgeDefinition {
  type: EdgeTypeId;
  label: string;
  description: string;
  defaultData: EdgeData;
  properties: readonly EdgePropertyFieldDefinition[];
}

const commonProperties: readonly EdgePropertyFieldDefinition[] = [
  { key: 'type', label: 'Tipo' },
  { key: 'label', label: 'Rótulo' },
  { key: 'style', label: 'Estilo da linha' },
  { key: 'animated', label: 'Animada' },
  { key: 'colorOverride', label: 'Cor' },
];

export const EDGE_DEFINITIONS: readonly EdgeDefinition[] = [
  {
    type: 'standard',
    label: 'Padrão',
    description: 'Fluxo normal entre duas etapas.',
    defaultData: { style: 'solid', animated: false, metrics: {} },
    properties: commonProperties,
  },
  {
    type: 'conditional',
    label: 'Condicional',
    description: 'Uma ramificação identificada a partir de uma decisão (por exemplo, Sim/Não).',
    defaultData: { style: 'dashed', animated: false, metrics: {} },
    properties: commonProperties,
  },
];

const registry = new Map<EdgeTypeId, EdgeDefinition>(
  EDGE_DEFINITIONS.map((definition) => [definition.type, definition]),
);

export function getEdgeDefinition(type: string): EdgeDefinition | undefined {
  return registry.get(type as EdgeTypeId);
}

export function getAllEdgeDefinitions(): readonly EdgeDefinition[] {
  return EDGE_DEFINITIONS;
}

export function validateEdgeRegistry(
  definitions: readonly EdgeDefinition[] = EDGE_DEFINITIONS,
): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const definition of definitions) {
    if (ids.has(definition.type)) errors.push(`Duplicate edge type: ${definition.type}`);
    ids.add(definition.type);
    if (!definition.label.trim()) errors.push(`Missing label: ${definition.type}`);
    if (definition.properties.length === 0)
      errors.push(`Missing property schema: ${definition.type}`);
  }
  for (const type of EDGE_TYPE_IDS) if (!ids.has(type)) errors.push(`Missing edge type: ${type}`);
  return errors;
}

export const EDGE_STYLE_LABELS = { solid: 'Sólida', dashed: 'Tracejada' } as const;
